<script lang="ts">
  import { type Writable, writable } from "svelte/store";
  import {
    type MyPagesSpot,
    type SearchSpots2,
    SortOrder,
    SortSpot,
    type Spot,
    SpotGroupFiltering,
    SpotStatusFiltering,
    type Tag,
  } from "$apis/parkito";
  import {
    EmptyState,
    LoadingIndicator,
    Pager,
    PagerComp,
    type Table,
    TableComp,
    ValInputField,
    ValSelect,
  } from "$components";
  import { onMount } from "svelte";
  import { Form, FormInput, Validate } from "$shared/lib/validation_next";
  import { withApi } from "$shared/lib/api-parkito";
  import { formatNumber } from "$shared/utils/misc";
  import { FadeIn, Hor, Spacer, Ver } from "$components";
  import { log } from "$shared/utils/misc";

  const PAGE_SIZE = 50;

  export let enableGroupFilter = false;
  export let enableStatusFilter = false;
  export let context: {
    queueId: string;
    siteTags: Tag[];
    showResidentalParkingPrices: boolean;
  };

  let currentPage: Array<Spot | MyPagesSpot> = [];
  let requestDuration: number | undefined;
  let fieldSearch = Form.field(Validate.len(0, 64));

  const groupFilterOptions = [
    { value: SpotGroupFiltering.ALL, label: "Alla" },
    { value: SpotGroupFiltering.ONLY_GROUPS, label: "Platsgrupper" },
    { value: SpotGroupFiltering.NON_GROUPED, label: "Ej grupperade" },
  ];
  const fieldGroupFilter = Form.multiSelect(groupFilterOptions);

  const statusFilterOptions = [
    { value: SpotStatusFiltering.ALL, label: "Alla" },
    { value: SpotStatusFiltering.FREE, label: "Lediga" },
    { value: SpotStatusFiltering.ACTIVE, label: "Aktiva" },
  ];
  const fieldStatusFilter = Form.multiSelect(statusFilterOptions);

  let params: SearchSpots2 = {
    groupFiltering: SpotGroupFiltering.NON_GROUPED,
    statusFiltering: SpotStatusFiltering.ALL,
    pagination: {
      page: 1,
      pageSize: PAGE_SIZE,
    },
    query: "",
    siteIds: [],
    sort: {
      by: SortSpot.NUMERIC_NAME,
      order: SortOrder.ASC,
    },
  };

  onMount(() => {
    fieldGroupFilter.subscribe((ev) => {
      if (ev.res.isOk() && ev.res.value !== params.groupFiltering) {
        params.groupFiltering = ev.res.value;
        doFetch();
      }
    });

    fieldStatusFilter.subscribe((ev) => {
      if (ev.res.isOk() && ev.res.value !== params.statusFiltering) {
        params.statusFiltering = ev.res.value;
        doFetch();
      }
    });

    fieldSearch.subscribe((ev) => {
      if (ev.res.isOk() && ev.res.value !== params.query) {
        params.query = ev.res.value;
        doFetch();
      }
    });
  });

  let tableSort: Table.Sort = {
    order: SortOrder.ASC,
    by: SortSpot.NUMERIC_NAME,
  };

  let tableData: Writable<Table.Data<6>> = writable({
    headers: [
      { title: "Namn", type: "text", sortKey: SortSpot.NAME },
      { title: "Område", type: "text" },
      {
        title: "Pris ex. moms",
        type: "number",
        sortKey: SortSpot.SUB_PRICE,
      },
      { title: "Pris ink. moms", type: "number" },
      { title: "Momsfri boendeparkering", type: "text", center: true },
      { title: "Taggar", type: "text" },
    ],
    rows: [],
  });

  // Pager data
  let pagerData: Pager.Data = Pager.defaultData();
  let isLoading = true;
  export let spotsOutput: FormInput<string[]>;
  let selectedIds = writable<string[]>($spotsOutput.res.unwrapOr([]));
  selectedIds.subscribe((ids) => spotsOutput.setValue(ids));

  log.info("initial spots", $spotsOutput);

  const doFetch = async () => {
    isLoading = true;
    const startTime = performance.now();
    const fetchedSpots = await withApi((api) =>
      api.queueSearchSpots(context.queueId, params)
    );
    const endTime = performance.now();
    requestDuration = endTime - startTime;

    if (fetchedSpots.isOk()) {
      pagerData = Pager.dataFromPaginated(fetchedSpots.value);
      currentPage = fetchedSpots.value.items;

      // Build table rows
      $tableData.rows = currentPage.map((spot: Spot | MyPagesSpot) => {
        return {
          id: spot.id,
          col: [
            spot.name,
            spot.area?.name ?? "—",
            formatNumber(spot.derivedPrice?.normal?.sub),
            !!spot.derivedPrice?.normal
              ? formatNumber(
                spot.derivedPrice?.normal?.sub +
                  spot.derivedPrice?.normal?.vat,
              )
              : "—",
            spot.derivedPrice?.residental?.sub &&
              spot.derivedPrice?.residental?.sub <
                spot.derivedPrice?.normal?.sub
              ? "Ja"
              : "Nej",
            spot.tagIds && spot.tagIds.length > 0
              ? spot.tagIds.map(
                (t) =>
                  context.siteTags.find((tt) => tt.id === t)?.name ?? "—",
              )
              : [],
          ],
        };
      });
    }

    isLoading = false;
  };

  export const reload = () => {
    if (import.meta.env.DEV) console.log("reload spots called");
    doFetch();
  };

  const onPageRequested = (page: number) => {
    params.pagination = {
      page: page,
      pageSize: params.pagination?.pageSize ?? PAGE_SIZE,
    };
    doFetch();
  };

  onMount(() => {
    doFetch();
  });

  $: {
    if (
      params.sort?.by !== tableSort.by ||
      params.sort?.order !== tableSort.order
    ) {
      params.sort = {
        by: tableSort.by as SortSpot,
        order: tableSort.order,
      };
      doFetch();
    }
  }
</script>

<div>
  <!-- Filter bar -->
  <div>
    <!-- Multiselect actions -->
    <!-- Filter: site -->
    <FadeIn>
      <Hor>
        <ValInputField
          label="Platsnamn"
          name="filter-text"
          field={fieldSearch}
          onEnter={() => {
            params.pagination = {
              page: 1,
              pageSize: params.pagination?.pageSize ?? PAGE_SIZE,
            };
            doFetch();
          }}
          customClass="mr-[8px]"
          placeholder="Sök plats"
        />
        {#if enableGroupFilter}
          <ValSelect
            label="Grupp"
            name="group-filter"
            field={fieldGroupFilter}
            customClass="mr-[8px]"
          />
        {/if}

        {#if enableStatusFilter}
          <ValSelect
            label="Status"
            name="status-filter"
            field={fieldStatusFilter}
            customClass="mr-[8px]"
          />
        {/if}
        <!-- Paginator -->
        <Spacer />
        <Ver>
          <Spacer />
          <PagerComp data={pagerData} {onPageRequested} />
        </Ver>
      </Hor>
    </FadeIn>
  </div>

  <!-- Table -->
  <div>
    {#if isLoading}
      <LoadingIndicator visible={true} customClass="py-[96px]" />
    {:else if currentPage.length === 0}
      <EmptyState
        title="Här var det tomt"
        message="Ingen data hittades med angivna filterparametrar"
        altMessage="Ingen data hittades"
        customClass="pb-[96px]"
      />
    {:else}
      <TableComp
        bind:tableData={$tableData}
        bind:sort={tableSort}
        bind:selectedIds
        enableSelect={true}
        let:prop={row}
      >
        <!-- Name -->
        <td>{row.col[0]}</td>
        <!-- Area -->
        <td>{row.col[1]}</td>
        <!--  Price without VAT -->
        <td class="text-console text-right">{row.col[2]}</td>
        <!--  Price with VAT -->
        <td class="text-console text-right">{row.col[3]}</td>
        <!--  VAT info -->
        <td class="text-center">{row.col[4]}</td>
        <!-- Tag -->
        <td>
          {#if !Array.isArray(row.col[5]) || row.col[5].length === 0}
            -
          {:else}
            {#each Array.isArray(row.col[5]) ? row.col[5] : [] as tag}
              <span class="mr-[8px]">{tag}</span>
            {/each}
          {/if}
        </td>
      </TableComp>
    {/if}
  </div>

  <!-- Request time counter -->
  {#if requestDuration && pagerData}
    <div class="w-full text-center py-[16px]">
      <span class="text-[#9FB1B2] text-sm">{pagerData.totalCount} träffar under
        {(requestDuration / 1000).toFixed(3)} s</span>
    </div>
  {/if}
</div>
